import './playerView.css';

import React, { useMemo } from 'react';
import { useEffect, useState, } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { userSettingsState } from '../../recoil/atoms/userSettingsState';
import { selectedPlayerState } from '../../recoil/atoms/selectedPlayerState';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { competitionsState } from '../../recoil/atoms/competitionsState';
import { tenantScopesState } from '../../recoil/atoms/tenantScopesState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';

import LockIcon from '@mui/icons-material/Lock';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PermScanWifiRoundedIcon from '@mui/icons-material/PermScanWifiRounded';
import BuildIcon from '@mui/icons-material/Build';
import BlockIcon from '@mui/icons-material/Block';

import { translate } from '../../../common/language/translations';
import { getTenantColor } from '../../static/tenantConfigs';
import { PlayerLinePlot } from '../plots/PlayerLinePlot';
import { PlayerRadarPlot } from '../plots/PlayerRadarPlot';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';
import { getIndexColor } from '../../utils/colorUtils';
import { getFormIcon, getPlayingTimeIcon } from '../../utils/iconUtils';
import { IconButton } from '../controls/buttons/IconButton';
import { ProgressCircle } from '../display/visualization/ProgressCircle';
import { SeasonTable } from '../tables/seasonTable/SeasonTable';
import { playerMetricMap } from '../../static/playerMetrics';
import { IndexBar } from '../display/visualization/IndexBar';
import { SkillcornerToggle } from '../controls/input/SkillcornerToggle';
import { getRoundedIndexValue, isLimitedRoleConfig, roleConfigIsBeingComputed } from '../../utils/metricUtils';
import { PlayerClubIteration } from '../../types/player';
import { PositionData, PositionGroupKey } from '../../types/position';
import { getRoleConfigPlayerViewFontSize } from '../../utils/fontSizeUtils';
import { getIterationsAndSelectedClubIterationId } from '../../utils/playerUtils';


interface PlayerViewStatisticsProps {
  iterationsWithEventData: PlayerClubIteration[] | undefined;
}


export const PlayerViewStatistics: React.FC<PlayerViewStatisticsProps> = ({
  iterationsWithEventData,
}) => {

  const userConfig = useRecoilValue(userConfigState);
  const userSettings = useRecoilValue(userSettingsState);
  const competitions = useRecoilValue(competitionsState);

  const selectedPlayer = useRecoilValue(selectedPlayerState);
  const { updateSelectedPlayer } = useSetSelectedPlayer();

  const tenantScopes = useRecoilValue(tenantScopesState);
  const tenantSettings = useRecoilValue(tenantSettingsState);
  const positionGroupToRoleIds = tenantSettings?.positionGroupToRoleIds;
  const roleConfigs = tenantSettings?.roleConfigs ?? {};

  const [hoveredRoleId, setHoveredRoleId] = useState<string | undefined>(undefined);

  const { openTextModal, openRoleInfoModal } = useOpenGlobalModal();

  const [seasonTableData, setSeasonTableData] = useState<PlayerClubIteration[] | undefined>(undefined);
  const [selectedClubIterationIdIsInitialized, setSelectedClubIterationIdIsInitialized] = useState(false);
  const [selectedClubIteration, setSelectedClubIteration] = useState<PlayerClubIteration | undefined>(undefined);
  const [shouldShowSeasonTable, setShouldShowSeasonTable] = useState(false);


  const positionGroupsPlayed: PositionGroupKey[] = useMemo(() => {
    if (selectedPlayer?.playerOverview?.position_groups) {
      return selectedPlayer.playerOverview.position_groups.length === 1
        ? selectedPlayer.playerOverview.position_groups
        : ['OVERALL', ...selectedPlayer.playerOverview.position_groups];
    }
    return [];
  }, [selectedPlayer?.playerOverview?.position_groups]);


  const selectedPositionGroupKey: PositionGroupKey = useMemo(() => {
    if (selectedPlayer?.selectedPositionGroup) return selectedPlayer.selectedPositionGroup;
    if (positionGroupsPlayed.length === 1) return positionGroupsPlayed[0];
    return 'OVERALL';
  }, [selectedPlayer?.selectedPositionGroup, positionGroupsPlayed]);


  const [showConfidence, setShowConfidence] = useState(false);


  useEffect(() => {
    if (iterationsWithEventData && userSettings && selectedPlayer?.id) {

      if (iterationsWithEventData.length > 0) {
        setShouldShowSeasonTable(true);
      }

      const { newIterations, newSelectedClubIterationId } = getIterationsAndSelectedClubIterationId(
        iterationsWithEventData,
        competitions,
        selectedPositionGroupKey,
        selectedPlayer.selectedClubIterationId,
        userSettings.seasonStatsToggles?.showOnlyDomesticLeagues,
        userSettings.seasonStatsToggles?.showOnlySeasonsWith180Minutes,
      );

      setSeasonTableData(newIterations);

      if (newSelectedClubIterationId !== selectedPlayer.selectedClubIterationId) {
        updateSelectedPlayer(selectedPlayer, { selectedClubIterationId: newSelectedClubIterationId });
      }

      const timer = setTimeout(() => {
        setSelectedClubIterationIdIsInitialized(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [iterationsWithEventData, competitions, selectedPositionGroupKey, userSettings, selectedPlayer?.id]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (selectedPlayer?.selectedClubIterationId) {
      const clubIteration = iterationsWithEventData?.find(iteration => iteration.club_iteration_id === selectedPlayer.selectedClubIterationId);
      setSelectedClubIteration(clubIteration);
    }
    else {
      setSelectedClubIteration(undefined);
    }
  }, [selectedPlayer?.selectedClubIterationId]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfidence(true);
    }, 200);

    return () => clearTimeout(timer);
  }, [selectedPositionGroupKey]);


  const getTopRowTitleSection = (
    widgetType: 'positionGroupsAndOverallIndexes' | 'formAndPlayingTime' | 'roleIndexes' | 'playerLinePlot' | 'playerRadarPlot',
  ) => {

    if (widgetType === 'positionGroupsAndOverallIndexes') {
      return (
        <div
          className='player-view-widget-title-container player-view-widget-title-container-small'
          style={{ width: 'calc(96.5% - 6px)', gap: 0 }}
        >
          <div className='player-view-statistics-position-group-column'>
            <div className='flex-row align-center' style={{ position: 'relative', gap: 2 }}>
              {translate('positionGroups', userConfig?.language)}
              <IconButton
                icon={<InfoOutlinedIcon style={{ fontSize: 12 }} />}
                onClick={() => openTextModal(
                  translate('positionGroups', userConfig?.language),
                  translate('positionGroupsInfo', userConfig?.language),
                  { infoType: 'infoIcon', titleKey: 'positionGroups' }
                )}
                size={15}
              />
            </div>
          </div>

          <div className='player-view-statistics-position-group-column'>
            <div className='flex-row align-center' style={{ position: 'relative', gap: 2 }}>
              {translate('skillIndex', userConfig?.language)}
              <IconButton
                icon={<InfoOutlinedIcon style={{ fontSize: 12 }} />}
                onClick={() => openTextModal(
                  playerMetricMap.skill_index.name[userConfig?.language ?? 'en'],
                  playerMetricMap.skill_index.description[userConfig?.language ?? 'en'],
                  { infoType: 'metric', titleKey: 'skill_index' }
                )}
                size={15}
              />
            </div>
          </div>

          <div className='player-view-statistics-position-group-column'>
            <div className='flex-row align-center' style={{ position: 'relative', gap: 2 }}>
              {translate(tenantScopes?.hasSkillcorner ? 'physicalIndex' : 'confidence', userConfig?.language)}
              <IconButton
                icon={<InfoOutlinedIcon style={{ fontSize: 12 }} />}
                onClick={() => openTextModal(
                  playerMetricMap[tenantScopes?.hasSkillcorner ? 'physical_index' : 'confidence'].name[userConfig?.language ?? 'en'],
                  playerMetricMap[tenantScopes?.hasSkillcorner ? 'physical_index' : 'confidence'].description[userConfig?.language ?? 'en'],
                  { infoType: 'metric', titleKey: tenantScopes?.hasSkillcorner ? 'physical_index' : 'confidence' }
                )}
                size={15}
              />
            </div>
          </div>
        </div>
      );
    }

    if (widgetType === 'formAndPlayingTime' || widgetType === 'roleIndexes') {
      return (
        <div
          className='player-view-widget-title-container player-view-widget-title-container-small'
          style={{ gap: 3 }}
        >
          {translate(widgetType, userConfig?.language)}
          <IconButton
            icon={<InfoOutlinedIcon style={{ fontSize: 12 }} />}
            onClick={() => openTextModal(
              title,
              translate(infoKey, userConfig?.language),
              { infoType: 'infoIcon', titleKey: widgetType }
            )}
            size={15}
          />
        </div>
      );
    }

    const isToggled = widgetType === 'playerRadarPlot'
      ? selectedPlayer?.isPhysicalRadarPlotToggled ?? false
      : selectedPlayer?.isPhysicalLinePlotToggled ?? false;

    const setIsToggled = widgetType === 'playerRadarPlot'
      ? (value: boolean) => updateSelectedPlayer(selectedPlayer, { isPhysicalRadarPlotToggled: value })
      : (value: boolean) => updateSelectedPlayer(selectedPlayer, { isPhysicalLinePlotToggled: value });

    let toggleStatus: 'blocked' | 'locked' | 'available' | undefined = undefined;

    let title = '';
    let infoKey = '';
    let progressIndex: number | undefined = undefined;

    if (widgetType === 'playerRadarPlot') {

      if (selectedPlayer && selectedClubIteration && competitions) {
        toggleStatus = selectedClubIteration?.physical_data_available ? 'available' : selectedClubIteration?.physical_data_exist ? 'locked' : 'blocked';

        const competition = competitions[selectedClubIteration.competition_id];

        if (competition) {
          title = competition.name + ' ' + selectedClubIteration.season;
        }

        const selectedPositionGroupKeyForIteration = selectedPositionGroupKey === 'OVERALL' && !selectedClubIteration.position_group_stats?.OVERALL
          ? selectedClubIteration.position_groups[0]
          : selectedPositionGroupKey;

        const minutesPlayed = selectedClubIteration.position_group_stats?.[selectedPositionGroupKeyForIteration]?.minutes_played ?? 0;
        progressIndex = Math.min(minutesPlayed / 150, 10);

        infoKey = 'playerRadarPlotInfo';
      }
      else {
        const titleKey = 'noSeasonSelected';
        title = translate(titleKey, userConfig?.language);
        infoKey = titleKey + 'Info';
      }
    }

    else {
      const positionGroupStats = selectedPlayer?.playerOverview?.position_group_stats?.[selectedPositionGroupKey];

      const skillIndexConfidence = positionGroupStats?.skill_index?.confidence ?? 0;
      const physicalIndexConfidence = positionGroupStats?.physical_index?.confidence ?? 0;
      const physicalIndexPossibleConfidence = positionGroupStats?.physical_index?.possible_confidence ?? 0;

      progressIndex = ((isToggled
        ? physicalIndexConfidence
        : skillIndexConfidence
      ) ?? 0) * 10;

      toggleStatus = physicalIndexConfidence > 0 ? 'available' : physicalIndexPossibleConfidence > 0 ? 'locked' : 'blocked';

      const titleKey = isToggled ? 'physicalRatingDevelopment' : 'skillRatingDevelopment';
      title = translate(titleKey, userConfig?.language);
      infoKey = titleKey + 'Info';
    }

    if (isToggled && toggleStatus !== 'available') {
      setIsToggled(false);
    }


    return (
      <div
        className='player-view-widget-title-container player-view-widget-title-container-large fade-in'
        style={{ justifyContent: 'space-evenly' }}
      >
        <div className='flex-row align-center' style={{ gap: 6 }}>

          {progressIndex !== undefined && (
            <div style={{ marginTop: -1, marginRight: 3 }}>
              <ProgressCircle
                size={14}
                progress={progressIndex * 10}
                color={getIndexColor(progressIndex, 60)}
                strokeWidth={9}
              />
            </div>
          )}

          {title}

          <IconButton
            icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
            onClick={() => openTextModal(
              title,
              translate(infoKey, userConfig?.language),
              { infoType: 'infoIcon', titleKey: widgetType }
            )}
            size={18}
          />
        </div>

        {!isGoalkeeper &&tenantScopes?.hasSkillcorner && toggleStatus !== undefined && (
          <div className='player-view-widget-title-divider' />
        )}

        {!isGoalkeeper && tenantScopes?.hasSkillcorner && toggleStatus !== undefined && (
          <div>
            <SkillcornerToggle
              isToggled={isToggled}
              setIsToggled={toggleStatus === 'available' ? setIsToggled : undefined}
              language={userConfig?.language ?? 'en'}
              toggleStatus={toggleStatus}
            />
          </div>
        )}
      </div>
    );
  };


  const tenantColor = getTenantColor(userConfig?.club ?? '');


  const roleIdsOfSelectedPositionGroup = selectedPositionGroupKey !== 'OVERALL'
    ? positionGroupToRoleIds?.[selectedPositionGroupKey] ?? []
    : [];


  const isGoalkeeper = selectedPlayer?.playerOverview?.position_info.primary_positions.includes('GK')
    || selectedPlayer?.playerOverview?.position_info.primary_positions.includes('G');


  return (
    <div className='full-size-container'>

      <div className='player-view-statistics-top-row'>

        <div className='player-view-statistics-position-and-index-container'>
          <div
            className='player-view-widget-container player-view-widget-container-absolute player-view-statistics-position-and-index-sub-container'
            style={{ height: (100 + ((positionGroupsPlayed.length - 1) * 70)) + '%' }}
          >
            {getTopRowTitleSection('positionGroupsAndOverallIndexes')}

            <div className='player-view-widget-content-container player-view-widget-content-container-small-title'>
              <div className='player-view-statistics-position-group-section'>
                {positionGroupsPlayed.map((positionGroup, positionGroupIndex) => {

                  const isSelected = positionGroup === selectedPositionGroupKey;
                  const positionGroupStats = selectedPlayer?.playerOverview?.position_group_stats?.[positionGroup];
                  const positions: PositionData[] = positionGroupStats?.position_info.positions ?? [];

                  const positionGroupSkillIndex = positionGroupStats?.skill_index;
                  const skillIndex = getRoundedIndexValue(positionGroupSkillIndex?.index);
                  const skillIndexConfidence = positionGroupSkillIndex?.confidence ?? 0;
                  const skillIndexPossibleConfidence = positionGroupSkillIndex?.possible_confidence ?? 0;

                  const positionGroupPhysicalIndex = positionGroupStats?.physical_index;
                  const physicalIndex = getRoundedIndexValue(positionGroupPhysicalIndex?.index);
                  const physicalIndexConfidence = positionGroupPhysicalIndex?.confidence ?? 0;
                  const physicalIndexPossibleConfidence = positionGroupPhysicalIndex?.possible_confidence ?? 0;

                  return (
                    <React.Fragment key={positionGroup}>

                      <div
                        className={
                          'player-view-statistics-position-group'
                          + (isSelected ? ' player-view-statistics-position-group-selected' : '')
                        }
                        style={{ borderColor: isSelected ? tenantColor : 'transparent' }}
                        onClick={() => !isSelected ? updateSelectedPlayer(selectedPlayer, { selectedPositionGroup: positionGroup }) : null}
                      >

                        <div
                          className='player-view-statistics-position-group-column'
                          style={{ fontSize: 13 - (positionGroup === 'OVERALL' ? 0 : positions.length) }}
                        >
                          {positionGroup === 'OVERALL' && (
                            <div>
                              {translate('all', userConfig?.language)}
                            </div>
                          )}

                          {positionGroup !== 'OVERALL' && positions.map(position => {
                            return (
                              <div key={position.position}>
                                {translate(position.position, userConfig?.language)}
                              </div>
                            );
                          })}
                        </div>

                        {skillIndexConfidence > 0 && (
                          <div className='player-view-statistics-position-group-column'>
                            <div className='flex-row align-center' style={{ width: '90%', maxWidth: 110, justifyContent: 'space-evenly' }}>
                              <div
                                className='flex-column align-center justify-center'
                                style={{
                                  color: isSelected ? getIndexColor(skillIndex, 60) : undefined,
                                  fontSize: isSelected ? 16 : 14,
                                  fontWeight: isSelected ? 500 : undefined
                                }}
                              >
                                <div className='flex-row align-center justify-center' style={{ height: 20 }}>
                                  {skillIndex}
                                </div>

                                <div className='flex-row align-center justify-center' style={{ height: 9 }}>
                                  <IndexBar
                                    index={showConfidence && isSelected ? skillIndex : 0}
                                    confidence={skillIndexConfidence}
                                    color={isSelected ? '#c2d3e6' : 'transparent'}
                                    maxWidth={46}
                                    height={2}
                                    secondDimension={'opacity'}
                                    trailColor={'#50566b'}
                                    duration={0.4}
                                  />
                                </div>
                              </div>

                              {tenantScopes?.hasSkillcorner && (
                                <ProgressCircle
                                  size={21}
                                  progress={showConfidence && isSelected ? skillIndexConfidence * 100 : 0}
                                  color={isSelected ? getIndexColor(skillIndexConfidence * 10, 60) : 'transparent'}
                                  duration={0.6}
                                  value={Math.round(skillIndexConfidence * 100)}
                                  fontSize={10}
                                  trailColor='#50566b'
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {skillIndexConfidence === 0 && skillIndexPossibleConfidence > 0 && (
                          <div className='player-view-statistics-position-group-column'>
                            <LockIcon style={{ fontSize: 20, color: '#ffffffaa' }} />
                          </div>
                        )}

                        {tenantScopes?.hasSkillcorner && physicalIndexConfidence > 0 && (
                          <div className='player-view-statistics-position-group-column'>
                            <div className='flex-row align-center' style={{ width: '90%', maxWidth: 110, justifyContent: 'space-evenly' }}>
                              <div
                                className='flex-column align-center justify-center'
                                style={{
                                  color: isSelected ? getIndexColor(physicalIndex, 60) : undefined,
                                  fontSize: isSelected ? 16 : 15,
                                  fontWeight: isSelected ? 500 : undefined
                                }}
                              >
                                <div className='flex-row align-center justify-center' style={{ height: 20 }}>
                                  {physicalIndex}
                                </div>

                                <div className='flex-row align-center justify-center' style={{ height: 9 }}>
                                  <IndexBar
                                    index={showConfidence && isSelected ? physicalIndex: 0}
                                    confidence={physicalIndexConfidence}
                                    color={isSelected ? '#c2d3e6' : 'transparent'}
                                    maxWidth={46}
                                    height={2}
                                    secondDimension={'opacity'}
                                    trailColor={'#50566b'}
                                    duration={0.4}
                                  />
                                </div>
                              </div>

                              <ProgressCircle
                                size={21}
                                progress={showConfidence && isSelected ? physicalIndexConfidence * 100 : 0}
                                color={isSelected ? getIndexColor(physicalIndexConfidence * 10, 60) : 'transparent'}
                                duration={0.6}
                                value={Math.round(physicalIndexConfidence * 100)}
                                fontSize={10}
                                trailColor='#50566b'
                              />
                            </div>
                          </div>
                        )}

                        {tenantScopes?.hasSkillcorner && physicalIndexConfidence === 0 && physicalIndexPossibleConfidence === 0 && (
                          <div className='player-view-statistics-position-group-column'>
                            <BlockIcon style={{ fontSize: 20, color: '#ffffff66' }} />
                          </div>
                        )}

                        {tenantScopes?.hasSkillcorner && physicalIndexConfidence === 0 && physicalIndexPossibleConfidence > 0 && (
                          <div className='player-view-statistics-position-group-column'>
                            <LockIcon style={{ fontSize: 20, color: '#ffffff66' }} />
                          </div>
                        )}

                        {!tenantScopes?.hasSkillcorner && (
                          <div className='player-view-statistics-position-group-column'>
                            <div className='flex-row align-center'>
                              <ProgressCircle
                                size={23}
                                progress={isSelected && showConfidence ? Math.round(skillIndexConfidence * 100) : 0}
                                color={isSelected ? getIndexColor(skillIndexConfidence * 10, 60) : 'transparent'}
                                duration={0.6}
                                value={Math.round(skillIndexConfidence * 100)}
                                fontSize={11}
                              />

                              {skillIndexConfidence < skillIndexPossibleConfidence && (
                                <div className='player-view-statistics-position-group-column-divider'>/</div>
                              )}

                              {skillIndexConfidence < skillIndexPossibleConfidence && (
                                <div style={{ color: isSelected ? '#ffffffbb' : undefined }}>
                                  <ProgressCircle
                                    size={23}
                                    progress={isSelected && showConfidence ? Math.round(skillIndexPossibleConfidence * 100) : 0}
                                    color={isSelected ? '#ffffff33' : 'transparent'}
                                    value={Math.round(skillIndexPossibleConfidence * 100)}
                                    fontSize={11}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {positionGroupIndex < positionGroupsPlayed.length - 1 &&
                        <div className='player-view-statistics-position-group-divider' />
                      }

                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          {(selectedPositionGroupKey === 'OVERALL' || positionGroupsPlayed.length === 1) && (
            <div className='player-view-widget-container player-view-widget-container-absolute player-view-statistics-position-and-index-sub-container'>
              {getTopRowTitleSection('formAndPlayingTime')}

              <div className='player-view-widget-content-container player-view-widget-content-container-small-title'>

                <div className='player-view-statistics-index-row'>
                  <div className='player-view-statistics-index-cell'>
                    <div
                      className='player-view-metric-label'
                      title={translate('showDescription', userConfig?.language)}
                      onClick={() => openTextModal(
                        translate('form', userConfig?.language),
                        translate('formInfo', userConfig?.language),
                        { infoType: 'metric', titleKey: 'form' }
                      )}
                    >
                      {translate('form', userConfig?.language)}
                    </div>

                    {getFormIcon(selectedPlayer?.playerOverview?.form_status ?? 'NO_FORM', 20, undefined, 65) ?? '-'}
                  </div>

                  <div className='player-view-statistics-index-cell'>
                    <div
                      className='player-view-metric-label'
                      title={translate('showDescription', userConfig?.language)}
                      onClick={() => openTextModal(
                        translate('playingTime', userConfig?.language),
                        translate('playingTimeInfo', userConfig?.language),
                        { infoType: 'metric', titleKey: 'playingTime' }
                      )}
                    >
                      {translate('playingTime', userConfig?.language)}
                    </div>

                    {getPlayingTimeIcon(selectedPlayer?.playerOverview?.availability_status ?? 'UNKNOWN', 20) ?? '-'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedPositionGroupKey !== 'OVERALL' && (
            <div className='player-view-widget-container player-view-widget-container-absolute player-view-statistics-position-and-index-sub-container'>
              {getTopRowTitleSection('roleIndexes')}

              <div className='player-view-widget-content-container player-view-widget-content-container-small-title'>

                <div className='player-view-statistics-index-row'>
                  {roleIdsOfSelectedPositionGroup.map(roleId => {

                    const roleConfig = roleConfigs[roleId];
                    if (!roleConfig) return null;

                    const isLimited = isLimitedRoleConfig(roleConfig);
                    const roleIndexIsBeingComputed = roleConfigIsBeingComputed(roleConfig);

                    // display options are roleIndex, BuildIcon, LockIcon
                    let value = undefined;
                    let confidence = undefined;
                    let color = undefined;

                    if (roleIndexIsBeingComputed) {
                      value = <BuildIcon style={{ fontSize: 17, color: '#ffffffaa' }} />;
                    }
                    else {
                      const positionGroupStats = selectedPlayer?.playerOverview?.position_group_stats?.[selectedPositionGroupKey];
                      const roleIndex = getRoundedIndexValue(positionGroupStats?.custom_indexes?.[roleId]?.index);
                      const roleIndexConfidence = positionGroupStats?.custom_indexes?.[roleId]?.confidence ?? 0;
                      const roleIndexPossibleConfidence = positionGroupStats?.custom_indexes?.[roleId]?.possible_confidence ?? 0;

                      if (roleIndexConfidence > 0) {
                        value = <div>{roleIndex}</div>;
                        confidence = roleIndexConfidence;
                        color = getIndexColor(roleIndex, 60);
                      }
                      else if (roleIndexPossibleConfidence > 0) {
                        value = <LockIcon style={{ fontSize: 17, color: '#ffffffaa' }} />;
                      }
                      else {
                        value = <BlockIcon style={{ fontSize: 17, color: '#ffffffaa' }} />;
                      }
                    }

                    return (
                      <div
                        key={roleId}
                        className='player-view-statistics-index-cell'
                      >
                        <div
                          className='player-view-metric-label flex-row align-center'
                          title={translate('showDescription', userConfig?.language)}
                          onClick={() => openRoleInfoModal(roleConfigs[roleId])}
                          style={{
                            fontSize: getRoleConfigPlayerViewFontSize(roleConfigs[roleId].name, isLimited, roleIdsOfSelectedPositionGroup.length),
                            height: 13,
                          }}
                        >
                          {isLimited && (
                            <PermScanWifiRoundedIcon style={{ fontSize: 11, marginRight: 2, color: '#ffffff99' }} />
                          )}
                          {roleConfigs[roleId].name}
                        </div>

                        <div
                          className='player-view-statistics-role-index-value'
                          onMouseEnter={() => setHoveredRoleId(roleId)}
                          onMouseLeave={() => setHoveredRoleId(undefined)}
                        >
                          <div
                            className='flex-row align-center justify-center'
                            style={{
                              color: color,
                              width: 25,
                              height: '100%',
                            }}
                          >
                            {value}
                          </div>

                          {(tenantScopes?.hasSkillcorner || isLimited) && confidence !== undefined && (
                            <div
                              className='flex-row align-center justify-center'
                              style={{
                                width: 25,
                                height: '100%',
                              }}
                            >
                              <ProgressCircle
                                size={hoveredRoleId === roleId ? 20 : 12}
                                progress={showConfidence ? confidence * 100 : 0}
                                color={getIndexColor(confidence * 10, 60)}
                                duration={0.6}
                                value={hoveredRoleId === roleId ? Math.round(confidence * 100) : undefined}
                                fontSize={10}
                                strokeWidth={hoveredRoleId === roleId ? undefined : 8}
                                trailColor='#50566b'
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {(positionGroupToRoleIds?.[selectedPositionGroupKey] ?? []).length === 0 && (
                    <div className='player-view-statistics-index-cell' style={{ color: '#ffffffbb' }}>
                      {translate('noRolesForPositionGroup', userConfig?.language)}
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}
        </div>

        <div className='player-view-statistics-plots-container'>
          <div className='player-view-widget-container player-view-widget-container-absolute player-view-statistics-line-plot-container'>
            {getTopRowTitleSection('playerLinePlot')}

            <div className='player-view-widget-content-container player-view-widget-content-container-large-title'>
              <div className='player-view-line-plot-container'>
                <PlayerLinePlot
                  playerClubIterations={seasonTableData ?? []}
                  selectedClubIterationId={selectedPlayer?.selectedClubIterationId}
                  selectedPositionGroupKey={selectedPositionGroupKey}
                  isPhysicalLinePlotToggled={selectedPlayer?.isPhysicalLinePlotToggled ?? false}
                  tenantColor={tenantColor}
                  competitions={competitions}
                />
              </div>
            </div>
          </div>

          <div className='player-view-widget-container player-view-widget-container-absolute player-view-statistics-radar-plot-container'>
            {selectedClubIterationIdIsInitialized && getTopRowTitleSection('playerRadarPlot')}

            <div className='player-view-widget-content-container player-view-widget-content-container-large-title'>
              <div className='player-view-radar-plot-container'>
                <PlayerRadarPlot
                  selectedClubIteration={selectedClubIteration}
                  selectedPositionGroupKey={selectedPositionGroupKey}
                  isPhysicalRadarPlotToggled={selectedPlayer?.isPhysicalRadarPlotToggled ?? false}
                  tenantColor={tenantColor}
                  language={userConfig?.language ?? 'en'}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='player-view-statistics-bottom-row'>
        {seasonTableData && shouldShowSeasonTable && (
          <div className='player-view-widget-container player-view-widget-container-absolute player-view-statistics-table-container'>
            <div key={selectedPositionGroupKey}>
              <SeasonTable
                data={seasonTableData}
                selectedPositionGroupKey={selectedPositionGroupKey}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
